import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, FormControl, FormErrorMessage, FormHelperText, FormLabel, Input, SimpleGrid, Text, VStack } from '@chakra-ui/react';

import { settingValidate } from './subscriberDataValidation';
import { formAttr, ISubscriberData } from './SubscriberData.d';
import FooterReferrals from 'components/footerReferrals/FooterReferrals';
import { useReferralProvider } from 'providers/referralsProvider/ReferralProvider';
import PhoneNumber from 'components/InputPhone/inputPhone';
import { normalizeString } from 'utils/functions/formatString';

const SubscriberData = () => {
  const {
    register,
    getValues,
    setValue,
    formState: { errors, isValid },
    setError,
    clearErrors,
    watch,
  } = useForm<ISubscriberData>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      codPais: '54'
    }
  });

  const navigate = useNavigate();
  const codPais = watch("codPais") || '';
  const codArea = watch("codArea") || '';
  const phone = watch("phone") || '';
  const nacionality = watch("nacionality") || '';

  const { referrals, companyData, subscriberData, editSubscriberData, cleanProvider } = useReferralProvider();
  const [buttonDisable, setButtonDisable] = useState<boolean>(false);

  const countryPhoneCodeLength: number = codPais.length || 2;
  const countryCodAreaLength: number = String(codArea).length;
  const phoneLength: number = String(phone).length;
  const showtextifArg: string = 'Ingresá el código de área sin el 0 y el número sin el 15';
  const maxCharacterPhone = (): number => countryPhoneCodeLength + countryCodAreaLength + phoneLength;

  const onBackButton = useCallback(() => cleanProvider(), [cleanProvider]);

  const formatCuit = useCallback((cuit: string): string => {
    if (!cuit) {
      return '';
    }
    const cuitNoDash: string = cuit.replace(/-/gi, '');
    return cuitNoDash.slice(0, 2) + '-' + cuitNoDash.slice(2, cuitNoDash.length - 1) + '-' + cuitNoDash.slice(-1);
  }, []);

  const onSubmit = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const referredData = {
      name: getValues('name').trim(),
      surname: getValues('surname').trim(),
      email: getValues('email'),
      codPais: getValues('codPais'),
      codArea: String(getValues('codArea')),
      phone: String(getValues('phone')),
      phoneNumber: `${getValues('codPais')}${getValues('codArea')}${getValues('phone')}`,
      nacionality: getValues('nacionality')
    };
    editSubscriberData(referredData);
    navigate('/datos');
  }, [getValues, editSubscriberData, navigate]);

  const handlerDisabledButton = () => {
    const name = getValues('name').trim();
    const surname = getValues('surname').trim();
    const email = getValues('email');

    const isFieldsValid = () => name && surname && email;

    const arePhoneFieldsValid = () =>
      isValid && codArea && phone && !errors?.codArea && !errors?.phone;

    const shouldDisableButton = isFieldsValid() && arePhoneFieldsValid();

    setButtonDisable(shouldDisableButton);
  };

  useEffect(() => {
    if (!companyData) {
      navigate('/referir-empresa');
      return;
    }
    if (subscriberData) {
      const { name, surname, email, phoneNumber, codArea, codPais, phone, nacionality } = subscriberData;
      setValue('name', name, { shouldValidate: true });
      setValue('surname', surname, { shouldValidate: true });
      setValue('email', email, { shouldValidate: true });
      setValue('codArea', Number(codArea), { shouldValidate: true });
      setValue('codPais', codPais, { shouldValidate: true });
      setValue('phone', Number(phone), { shouldValidate: true });
      setValue('phoneNumber', phoneNumber, { shouldValidate: true });
      setValue('nacionality', nacionality, { shouldValidate: true });
      setButtonDisable(true)
    }
  }, [navigate]);

  const handleNumberFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === 'nacionality') {
      setValue(name as formAttr, value, { shouldValidate: true });
    } else {
      const numbers = value.match(/\d+/g);
      const result: string = normalizeString(numbers ? numbers.join('') : '');
      setValue(name as formAttr, result, { shouldValidate: true });
    }
    handleFieldValidation(event);
    handlerDisabledButton();
  };

  const validateNumber = (name: formAttr, value: string) => {
    const validationRules = {
      codPais: () => maxCharacterPhone() <= 14,
      phone: () => maxCharacterPhone() <= 14 && (getValues('phone') >= 4 || ''),
      codArea: () => maxCharacterPhone() <= 14 && (getValues('codArea') >= 2 || ''),
    };

    const validatorsMessage = 'El formato del teléfono no es válido'

    for (const property in validationRules) {
      if (!validationRules[property]()) {
        handleFieldError(property as formAttr, { message: validatorsMessage }, value);
      } else {
        handlerClearsErrorPhone(name);
      }
    }
    handlerDisabledButton();
  };

  const isFieldName = (name: string): name is formAttr => {
    return [
      "codArea", "name", "surname", "email", "phoneNumber", "codPais", "phone", "nacionality"
    ].includes(name as formAttr);
  };

  const handleFieldError = useCallback((name: formAttr, error = { message: '' }, value?: string): void => {
    if (isFieldName(name)) {
      setError(name, { type: "manual", message: error.message });
    }
  }, [setError]);

  const handleFieldValidation = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value.trim() === '') {
      handleFieldError(name as formAttr, { message: 'Este campo es requerido' }, value);
    } else if (name === 'codArea' || name === 'phone') {
      validateNumber(name as formAttr, value);
    } else {
      handleFieldError(name as formAttr, { message: 'Este campo es requerido' }, value);
    }
  };

  const maxLengthcodArea = (): number => {
    if ((phoneLength === 10 && countryPhoneCodeLength === 3) || (phoneLength === 9 && countryPhoneCodeLength === 4)) return 1
    if ((phoneLength === 10 && countryPhoneCodeLength === 2) || (phoneLength === 9 && countryPhoneCodeLength === 3) || (phoneLength === 8 && countryPhoneCodeLength === 4)) return 2
    if ((phoneLength === 9 && countryPhoneCodeLength === 2) || (phoneLength === 8 && countryPhoneCodeLength === 3) || (phoneLength === 7 && countryPhoneCodeLength === 4)) return 3
    if (countryPhoneCodeLength === 3 && countryCodAreaLength === 4) return 4
    return 4
  };

  const maxLengthNumber = (): number => {
    if (countryPhoneCodeLength === 2 && countryCodAreaLength === 2) return 10
    if ((countryPhoneCodeLength === 2 && countryCodAreaLength === 3) || (countryPhoneCodeLength === 3 && countryCodAreaLength === 2) || (countryPhoneCodeLength === 4 && countryCodAreaLength === 1)) return 9
    if (countryPhoneCodeLength === 3 && countryCodAreaLength === 4) return 7
    if ((countryPhoneCodeLength === 3 && countryCodAreaLength === 3) || (countryPhoneCodeLength === 2 && countryCodAreaLength === 4) || (countryPhoneCodeLength === 4 && countryCodAreaLength === 2)) return 8
    if (countryPhoneCodeLength === 4 && countryCodAreaLength === 4) return 6
    if ((countryPhoneCodeLength === 4 && countryCodAreaLength === 3) || (countryPhoneCodeLength === 3 && countryCodAreaLength === 4)) return 7
    return 10
  };

  const handlerClearsErrorPhone = useCallback((name) => {
    clearErrors(name);
    clearErrors('codPais');
  }, [clearErrors]);


  useEffect(() => {
    handlerDisabledButton()
  }, [subscriberData, companyData, navigate, setValue, handlerDisabledButton]);

  return (
    <>
      <SimpleGrid columns={3} overflowY='auto' maxH='calc(100vh - 187px)'>
        <Box data-testid='left-offset'></Box>
        <Box>
          <Flex flexDir='column'>
            <Text textStyle='h2Bold' textAlign='center' mb='4px' mt={4} color='neutral.130'>
              Referí una empresa
            </Text>
            <Text textStyle='bodyMd' textAlign='center' color='neutral.130'>
              Completá los datos para recomendar Interbanking.
            </Text>
            <Text data-testid='referrals-text' textStyle='bodyMd' textAlign='center' color='neutral.130'>
              Pódes referir hasta {referrals} empresas por día.
            </Text>
          </Flex>
          <Flex flexDir='column'>
            <Text textStyle='titleMdBold' pt={4} pb={4} color='neutral.130'>
              ¿A qué empresa querés referir?
            </Text>
            <Box borderWidth={1} borderColor='#656E84' borderRadius={12} p={6}>
              <Text color='secondary.100' fontSize='10px' fontWeight={600} lineHeight='12.1px' letterSpacing='1.2px'>CUIT</Text>
              <Text color='#292D36' fontSize='16px' textStyle='labelMdBold' pt={2}>{formatCuit(companyData?.cuit)}</Text>
              {companyData?.code === 'REF_ALLOWED_TO_UPGRADE' && (
                <Flex flexDir='row' pt={1}>
                  <Text color='secondary.400' fontSize='13px' fontWeight={500} lineHeight='17px' letterSpacing='1.2px'>Podés referir a esta empresa para que </Text>
                  <Text color='secondary.400' fontSize='13px' fontWeight={700} lineHeight='17px' letterSpacing='1.2px' ml={1}> mejore su plan.</Text>
                </Flex>
              )}
            </Box>
          </Flex>
          <Flex flexDir='column' pt={4}>
            <Text textStyle='titleMdBold' color='neutral.130'>
              Ingresá los datos de un contacto en la empresa
            </Text>
            <Text textStyle='bodyMd' color='neutral.130'>Te recomendamos que sea una persona que pueda tomar la decisión de contratar Interbanking.</Text>
            <VStack spacing={4} mt='16px' justifyContent='center'>
              <FormControl isInvalid={!!errors.name}>
                <FormLabel textStyle='labelMd' display='contents' color='#2F3034'>
                  Nombre
                </FormLabel>
                <Input
                  data-testid='name-input'
                  id='name'
                  placeholder='Nombre'
                  maxLength={50}
                  {...register('name', {
                    ...settingValidate.name,
                    onBlur: e => setValue('name', e.target.value.trim(), { shouldValidate: true }),
                    onChange: () => handlerDisabledButton()
                  })}
                />
                <FormErrorMessage textStyle='labelSm' mt={0}>
                  {errors?.name?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.surname}>
                <FormLabel textStyle='labelMd' display='contents' color='#2F3034'>
                  Apellido
                </FormLabel>
                <Input
                  data-testid='surname-input'
                  id='surname'
                  placeholder='Apellido'
                  maxLength={50}
                  {...register('surname', {
                    ...settingValidate.surname,
                    onBlur: e => setValue('surname', e.target.value.trim(), { shouldValidate: true }),
                    onChange: () => handlerDisabledButton()
                  })}
                />
                <FormErrorMessage textStyle='labelSm' mt={0}>
                  {errors?.surname?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel textStyle='labelMd' display='contents' color='#2F3034'>
                  Email
                </FormLabel>
                <Input
                  data-testid='email-input'
                  placeholder='Email'
                  maxLength={50}
                  {...register('email', {
                    ...settingValidate.email,
                    onBlur: e => setValue('email', e.target.value.trim(), { shouldValidate: true }),
                    onChange: () => handlerDisabledButton()
                  })}
                />
                {!errors?.email ? (
                  <FormHelperText textStyle='labelSm' mt={0} color='neutral.90'>
                    Enviaremos un email a esta dirección para completar el proceso
                  </FormHelperText>
                ) : (
                  <FormErrorMessage textStyle='labelSm' mt={0}>
                    {errors?.email?.message}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl isInvalid={errors.phone !== undefined}>
                <PhoneNumber
                  values={{ codArea, phone, nacionality }}
                  handleFieldValidation={handleFieldValidation}
                  handleNumberFieldChange={handleNumberFieldChange}
                  maxLengthcodArea={maxLengthcodArea()}
                  maxLengthNumber={maxLengthNumber()}
                  error={!!errors.codPais || !!errors.codArea || !!errors.phone}
                  errorMessage={errors.phone?.message || errors.codArea?.message || errors.codPais?.message || showtextifArg}
                />
              </FormControl>
            </VStack>
          </Flex>
        </Box>
        <Box data-testid='right-offset'></Box>
      </SimpleGrid>
      <FooterReferrals
        stepNumber='2'
        textContinue='Continuar'
        onAction={onSubmit}
        onBackButton={onBackButton}
        disableContinue={!buttonDisable}
      />
    </>
  );
};

export default SubscriberData;